import * as React from 'react';
import { connect } from 'react-redux';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import classnames from 'classnames';

import Spinner from 'components/spinner/Spinner';

import ListsDashboardItem from 'modules/lists/components/item/ListsDashboardItem';
import ListDashboardFilters from './dashboard-filters/ListDashboardFilters';
import ListDashboardEmptyMessage from './empty-message/ListDashboardEmptyMessage';

import { getActiveBrandFilterSelector } from '../../../selectors/brand-filters/brandFilters';

import {
  CUSTOM_LIST_TYPE,
  LIST_DASHBOARD_QUERY_PARAMS,
  getManageListStatus,
  getQueryValueFromUrl,
  getSortOptions,
  isCustomList
} from 'utils/list';

import type { List, ListsContentType, LocationWindow } from 'types';
import type { ListDashboardTabs } from 'utils/list';

type OwnProps = {
  contentType?: ListsContentType,
  location: LocationWindow,
  hideFilters?: boolean,
  onDelete: () => void,
  onDuplicate?: () => void,
  onAddList: () => void
};

type StateProps = {
  activeBrandFilter: string,
  isLoading: boolean,
  isSearchingOrSorting: boolean,
  lists: Array<List>,
  organizationsListIds: Array<number>
};

type Props = OwnProps & StateProps;

const { PAGE, SEARCH, TAB, FILTER } = LIST_DASHBOARD_QUERY_PARAMS;

class ListDashboardResults extends React.Component<Props> {
  getQueryValue(type: string): string {
    const { location } = this.props;

    return getQueryValueFromUrl(location, type);
  }

  renderSpinner() {
    return (
      <div className="lists-dashboard__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  renderEmptyMessage(type: 'custom' | 'smart' | 'pagination' | 'search'): React.ReactNode {
    const { location, onAddList } = this.props;
    return (
      <ListDashboardEmptyMessage
        type={type}
        location={location}
        onAddList={onAddList}
      />
    );
  }

  renderResults(): React.ReactNode {
    const { contentType, isSearchingOrSorting, lists, organizationsListIds, onDelete, onDuplicate } = this.props;

    if (isSearchingOrSorting) {
      return this.renderSpinner();
    }

    if (isEmpty(lists)) {
      return this.renderEmptyMessage('search');
    }

    return lists.map((item, index) => {
      const orgId = item.organization?.id || item?.brandProfile?.organizationId;

      if (isNil(orgId)) {
        return null;
      }

      const isManageableByOrganization = getManageListStatus(orgId, organizationsListIds);
      const manageListStatus = isManageableByOrganization && isCustomList(item);

      return (
        <ListsDashboardItem
          key={`${item.id}-${orgId}-${index}`}
          contentType={contentType}
          item={item}
          orgId={orgId}
          canManageList={manageListStatus}
          isManageableByOrganization={isManageableByOrganization}
          onDelete={onDelete}
          onDuplicate={onDuplicate}
        />
      );
    });
  }

  render() {
    const { isLoading, isSearchingOrSorting, location, lists, hideFilters, activeBrandFilter, onAddList } = this.props;
    const searchTerm = this.getQueryValue(SEARCH);
    const page = this.getQueryValue(PAGE);
    const tab = this.getQueryValue(TAB) as ListDashboardTabs;
    const filter = this.getQueryValue(FILTER) || activeBrandFilter;
    const noListsAvailable = isEmpty(lists);
    const resultItemsClass = classnames('lists-dashboard-results__items', {
      'lists-dashboard-results__items--empty': noListsAvailable,
      'lists-dashboard-results__items--is-done-loading': !isLoading && !isSearchingOrSorting
    });

    if (isLoading) {
      return this.renderSpinner();
    }

    if (noListsAvailable && !searchTerm.length && !isSearchingOrSorting) {
      if (Number(page) > 1) {
        return this.renderEmptyMessage('pagination');
      }
      if (tab === 'smart') {
        return this.renderEmptyMessage('smart');
      }

      return this.renderEmptyMessage('custom');
    }

    return (
      <React.Fragment>
        {
          !hideFilters && (
            <ListDashboardFilters
              location={location}
              sortOptions={getSortOptions(tab, filter)}
              onAddList={tab === CUSTOM_LIST_TYPE ? onAddList : undefined}
            />
          )
        }
        <div className={resultItemsClass}>
          {this.renderResults()}
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state): StateProps => {
  const { organizations, lists } = state;
  return ({
    activeBrandFilter: getActiveBrandFilterSelector(state),
    organizationsListIds: organizations.list.map(organization => organization.id),
    lists: lists.all,
    isLoading: lists.isLoading,
    isSearchingOrSorting: lists.isSearchingOrSorting
  });
};

export default connect(mapStateToProps)(ListDashboardResults);
